import { getI18n } from '@/plugins/i18n'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useProductService } from '@/services/product.service'
import { useWebSiteStore } from '@/stores/web-site.store'
import { setDepositValue } from '@/composables/registry/warehouse-document-grid'

export const useColumns = (supplierBrand, keepDescription, currency, documentRowType, productUnitOfMeasure, includesVat, onDepositChanged = () => {}, warehouseDocument = false, options = {}) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setPriceVatValue, setTotalNoVatValue, setTotalVatValue } = useProductService()

  const requiresUnit = e => ![webSiteStore.documentRowTypesEnum.PRODUCT, webSiteStore.documentRowTypesEnum.FREE].includes(e.data.document_row_type) || !!e.value
  const requiresVat = e => e.data.document_row_type === webSiteStore.documentRowTypesEnum.TEXT || !!e.value
  const differentDepositRule = otherField => ({
    type: 'custom', reevaluate: true,
    message: i18n.t('error.validation.must_be_different'),
    validationCallback: e => !options.isTransfer?.value || !e.value || e.value !== e.data[otherField],
  })

  return [
    { type: 'buttons', buttons: [{ template: 'editButtonsTemplate' }], width: warehouseDocument ? '6rem' : '4rem', visibleIndex: 0, fixed: false },
    dataGridColumn.useRowNumberColumn({ width: '8rem' }),
    dataGridColumn.useDocumentRowTypeColumn({ productUnitOfMeasure }),
    dataGridColumn.useSupplierBrandColumn({ supplierBrand, documentRowType }),
    ...dataGridColumn.useProductColumns({ supplierBrand, keepDescription, productUnitOfMeasure }),
    dataGridColumn.useDescriptionColumn({ validationRules: [{ type: 'required' }] }),
    dataGridColumn.useNotesColumn(),
    dataGridColumn.useQuantityColumn(),
    ...(warehouseDocument ? [dataGridColumn.useQuantityColumn({ dataField: 'expected_quantity', caption: i18n.t('warehouseDocument.receiptSummary.expectedQuantity') })] : []),
    dataGridColumn.useUnitOfMeasureColumn({ productUnitOfMeasure, showClearButton: false, validationRules: [{ type: 'custom', message: i18n.t('common.required'), reevaluate: true, validationCallback: requiresUnit }] }),
    dataGridColumn.useCostColumn({ validationRules: [] }),
    dataGridColumn.useCostValorizationColumn(),
    dataGridColumn.useDiscountCategoryColumn(),
    dataGridColumn.usePriceColumn({ dataField: 'product_price', caption: i18n.t('common.listing'), currency, validationRules: [], visible: false, showInColumnChooser: false }),
    dataGridColumn.usePriceColumn({ caption: i18n.t('common.unitPriceNoVat'), currency }),
    dataGridColumn.usePriceColumn({ dataField: 'price_vat', caption: i18n.t('common.unitPriceVat'), currency, setCellValue: setPriceVatValue }),
    dataGridColumn.useChargePercentageColumn({ caption: i18n.t('common.chargePercentageAlt') }),
    dataGridColumn.useDiscountColumn({ includesVat: includesVat.value }),
    dataGridColumn.useDiscount2Column({ includesVat: includesVat.value }),
    dataGridColumn.useDiscount3Column({ includesVat: includesVat.value }),
    { dataField: 'total_discount', dataType: 'number', visible: false, showInColumnChooser: false },
    ...dataGridColumn.useVatTypeColumns({ validationRules: [{ type: 'custom', message: i18n.t('common.required'), reevaluate: true, validationCallback: requiresVat }] }),
    dataGridColumn.useTotalNoVatColumn({ currency, setCellValue: setTotalNoVatValue }),
    dataGridColumn.useTotalVatColumn({ currency, setCellValue: (data, value, current) => setTotalVatValue(data, value, current, includesVat.value) }),
    dataGridColumn.useDepositColumn({
      visible: false, showInColumnChooser: false, validationRules: [differentDepositRule('garage')],
      setCellValue: (data, value, currentData) => {
        if (setDepositValue(data, value, currentData, 'deposit', 'garage', options.isTransfer?.value)) {
          options.onDestinationDepositChanged?.(data.garage)
        }
        onDepositChanged(data.deposit)
      },
    }),
    dataGridColumn.useDepositColumn({
      dataField: 'garage', caption: i18n.t('common.garage'), visible: false, showInColumnChooser: false,
      validationRules: [differentDepositRule('deposit')],
      setCellValue: (data, value, currentData) => {
        if (setDepositValue(data, value, currentData, 'garage', 'deposit', options.isTransfer?.value)) {
          onDepositChanged(data.deposit)
        }
        options.onDestinationDepositChanged?.(data.garage)
      },
    }),
    { dataField: 'timeEquivalent', visible: false, showInColumnChooser: false, editorOptions: { readOnly: true } },
    { dataField: 'stock_position', visible: false, showInColumnChooser: false },
  ]
}
